import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Moon, LogOut, PlusCircle, Heart, MessageCircle, User, Trash2, ChevronDown, ChevronUp, Image, Settings, Globe, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import LanguageSelector from "@/components/LanguageSelector";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CommunityFeed({ user, onLogout }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentingOn, setCommentingOn] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [showComments, setShowComments] = useState({});
  const [expandedDreams, setExpandedDreams] = useState({});
  const [artworks, setArtworks] = useState({});
  const [loadingArt, setLoadingArt] = useState({});
  const [translations, setTranslations] = useState({});
  const [loadingTranslation, setLoadingTranslation] = useState({});

  useEffect(() => {
    fetchFeed();
  }, []);

  const fetchFeed = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/feed`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeed(response.data);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (dreamId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/dreams/${dreamId}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update feed
      setFeed(feed.map(item => 
        item.id === dreamId 
          ? { ...item, liked_by_me: response.data.liked, likes_count: item.likes_count + (response.data.liked ? 1 : -1) }
          : item
      ));
      
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const toggleComments = async (dreamId) => {
    if (showComments[dreamId]) {
      setShowComments({ ...showComments, [dreamId]: null });
    } else {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/dreams/${dreamId}/comments`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setShowComments({ ...showComments, [dreamId]: response.data });
      } catch (error) {
        toast.error(t('common.error'));
      }
    }
  };

  const handleComment = async (dreamId) => {
    if (!commentText.trim()) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/dreams/${dreamId}/comments?content=${encodeURIComponent(commentText)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Add comment to list
      setShowComments({
        ...showComments,
        [dreamId]: [response.data, ...(showComments[dreamId] || [])]
      });
      
      setCommentText("");
      setCommentingOn(null);
      toast.success(t('toast.commentAdded'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const toggleExpand = (dreamId) => {
    setExpandedDreams(prev => ({ ...prev, [dreamId]: !prev[dreamId] }));
  };

  const trackView = async (dreamId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/dreams/${dreamId}/view`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeed(prev => prev.map(d => d.id === dreamId ? { ...d, views: res.data.views } : d));
    } catch {}
  };

  const viewedRef = useRef(new Set());
  const observerRef = useRef(null);

  const dreamCardRef = useCallback((node) => {
    if (!node) return;
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const dreamId = entry.target.dataset.dreamId;
              if (dreamId && !viewedRef.current.has(dreamId)) {
                viewedRef.current.add(dreamId);
                trackView(dreamId);
              }
            }
          });
        },
        { threshold: 0.5 }
      );
    }
    observerRef.current.observe(node);
  }, []);

  const loadArtwork = async (dreamId) => {
    if (artworks[dreamId] || loadingArt[dreamId]) return;
    setLoadingArt(prev => ({ ...prev, [dreamId]: true }));
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/dreams/${dreamId}/artwork`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArtworks(prev => ({ ...prev, [dreamId]: `data:${res.data.mime_type};base64,${res.data.image}` }));
    } catch {
      // No artwork available
    } finally {
      setLoadingArt(prev => ({ ...prev, [dreamId]: false }));
    }
  };

  const translateDream = async (dreamId, content, title) => {
    if (translations[dreamId] || loadingTranslation[dreamId]) return;
    setLoadingTranslation(prev => ({ ...prev, [dreamId]: true }));
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/dreams/translate`, 
        { dream_id: dreamId, content, title, target_language: "english" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTranslations(prev => ({ ...prev, [dreamId]: res.data }));
    } catch {
      toast.error(t('feed.translateError') || 'Käännös epäonnistui');
    } finally {
      setLoadingTranslation(prev => ({ ...prev, [dreamId]: false }));
    }
  };


  const handleDeleteComment = async (dreamId, commentId) => {
    if (!window.confirm("Haluatko varmasti poistaa tämän kommentin?")) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Remove comment from list
      setShowComments({
        ...showComments,
        [dreamId]: showComments[dreamId].filter(c => c.id !== commentId)
      });
      
      toast.success("Kommentti poistettu!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Virhe poistettaessa");
    }
  };

  const handleDeleteDream = async (dreamId) => {
    if (!window.confirm("Haluatko varmasti poistaa tämän unen ja kaikki sen kommentit?")) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/dreams/${dreamId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Remove dream from feed
      setFeed(feed.filter(d => d.id !== dreamId));
      
      toast.success("Uni poistettu!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Virhe poistettaessa");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="glass-effect border-b border-white/60 sticky top-0 z-50 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
                <img src="/logo-new.png" alt="Uneksia" className="w-12 h-12" />
                <span className="text-2xl font-heading font-light text-void tracking-tight">{t('appName')}</span>
              </div>
              <div className="hidden md:flex items-center gap-4">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/dashboard')}
                  className="font-body text-void hover:text-void/80"
                  data-testid="nav-dashboard"
                >
                  {t('nav.dashboard')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/dreams')}
                  className="font-body text-void hover:text-void/80"
                  data-testid="nav-dreams"
                >
                  {t('nav.myDreams')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/community-hub')}
                  className="font-body text-lucid hover:text-lucid/80 font-medium"
                  data-testid="nav-community-hub"
                >
                  {t('nav.community')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/blog")}
                  className="font-body text-void hover:text-void/80"
                  data-testid="nav-blog"
                >
                  {t("blog.title")}
                </Button>
                {user?.is_admin && (
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/admin')}
                    className="font-body text-red-500 hover:text-red-600 font-medium"
                    data-testid="nav-admin"
                  >
                    Admin
                  </Button>
                )}
                
                {!user?.is_premium && (
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/premium')}
                    className="font-body text-lucid hover:text-lucid/80 font-medium"
                    data-testid="nav-premium"
                  >
                    ✨ {t('nav.premium')}
                  </Button>
                )}
                
              </div>
            </div>
            <div className="flex items-center gap-4">
              <LanguageSelector variant="minimal" />
              <Button
                onClick={() => navigate('/journal')}
                className="bg-void text-white hover:bg-void/90 rounded-full font-body font-medium transition-all duration-300 hover:scale-105 active:scale-95"
                data-testid="new-dream-button"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                {t('nav.newDream')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/settings')}
                className="text-void hover:text-void/80"
                data-testid="settings-button"
              >
                <Settings className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                onClick={onLogout}
                className="text-void hover:text-void/80"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-heading font-light text-void tracking-tight mb-2">
            {t('feed.title')} <span className="italic">{t('feed.titleItalic')}</span>
          </h1>
          <p className="text-lg text-void/70 font-body tracking-wide">
            {t('feed.subtitle')}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-void/60 font-body">{t('feed.loading')}</p>
          </div>
        ) : feed.length === 0 ? (
          <div className="glass-effect rounded-3xl p-12 text-center">
            <p className="text-void/60 font-body mb-4">{t('feed.noPublicDreams')}</p>
            <Button
              onClick={() => navigate('/journal')}
              className="bg-void text-white hover:bg-void/90 rounded-full font-body"
            >
              {t('feed.shareFirst')}
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {feed.map((dream) => (
              <div key={dream.id} ref={dreamCardRef} data-dream-id={dream.id} className="glass-effect rounded-3xl p-6 hover:shadow-lg transition-all duration-300">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-ethereal/20 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-void" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-body font-medium text-void">{dream.user_name}</span>
                        {dream.user_is_premium && (
                          <span className="text-xs px-2 py-0.5 bg-lucid/20 text-void rounded-full">✨ Premium</span>
                        )}
                      </div>
                      <span className="text-sm text-void/60 font-body">{formatDate(dream.created_at)}</span>
                    </div>
                  </div>
                  {user?.is_admin && (
                    <button
                      onClick={() => handleDeleteDream(dream.id)}
                      className="text-red-400 hover:text-red-600 transition-colors p-2 rounded-full hover:bg-red-50"
                      title="Poista uni (Admin)"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>

                {/* Content */}
                <div className="mb-4">
                  <h3 className="text-xl font-heading font-light text-void mb-2 cursor-pointer hover:text-void/80" onClick={() => navigate(`/dreams/${dream.id}`)}>{dream.title}</h3>
                  
                  {/* Dream text - expandable */}
                  <p className={`text-void/80 font-body ${expandedDreams[dream.id] ? '' : 'line-clamp-3'}`}>
                    {dream.content}
                  </p>
                  {dream.content && dream.content.length > 200 && (
                    <button
                      onClick={() => toggleExpand(dream.id)}
                      className="flex items-center gap-1 text-sm text-lucid hover:text-lucid/80 font-body mt-2 transition-colors"
                      data-testid={`expand-dream-${dream.id}`}
                    >
                      {expandedDreams[dream.id] ? (
                        <><ChevronUp className="w-4 h-4" /> {t('feed.showLess') || 'Näytä vähemmän'}</>
                      ) : (
                        <><ChevronDown className="w-4 h-4" /> {t('feed.readMore') || 'Lue lisää'}</>
                      )}
                    </button>
                  )}

                  {/* Dream Artwork */}
                  {dream.has_artwork && (
                    <div className="mt-4">
                      {artworks[dream.id] ? (
                        <img
                          src={artworks[dream.id]}
                          alt={dream.title}
                          className="w-full rounded-2xl shadow-lg max-h-96 object-cover"
                          data-testid={`dream-artwork-${dream.id}`}
                        />
                      ) : (
                        <button
                          onClick={() => loadArtwork(dream.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-xl font-body text-sm transition-colors"
                          data-testid={`load-artwork-${dream.id}`}
                        >
                          <Image className="w-4 h-4" />
                          {loadingArt[dream.id] ? (t('common.loading') || 'Ladataan...') : (t('feed.viewArtwork') || 'Näytä unitaide')}
                        </button>
                      )}
                    </div>
                  )}
                  
                  {/* Tags */}
                  {dream.tags && dream.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {dream.tags.slice(0, 3).map((tag, idx) => (
                        <span key={idx} className="text-xs px-3 py-1 bg-lucid/20 text-void rounded-full font-body">
                          {t(`tags.${tag}`) || tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Translate button */}
                  <div className="mt-3">
                    {translations[dream.id] ? (
                      <div className="bg-blue-50/60 rounded-xl p-3 border border-blue-100">
                        <p className="text-xs text-blue-500 font-medium mb-1 flex items-center gap-1">
                          <Globe className="w-3 h-3" /> English
                        </p>
                        <p className="text-sm font-heading font-medium text-void mb-1">{translations[dream.id].title}</p>
                        <p className={`text-sm text-void/80 font-body ${expandedDreams[dream.id] ? '' : 'line-clamp-3'}`}>{translations[dream.id].content}</p>
                      </div>
                    ) : (
                      <button
                        onClick={() => translateDream(dream.id, dream.content, dream.title)}
                        className="flex items-center gap-1.5 text-xs text-blue-500 hover:text-blue-700 font-body transition-colors"
                        data-testid={`translate-dream-${dream.id}`}
                      >
                        <Globe className="w-3.5 h-3.5" />
                        {loadingTranslation[dream.id] ? (t('common.loading') || 'Ladataan...') : (t('feed.translate') || 'Translate to English')}
                      </button>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-6 pt-4 border-t border-white/30">
                  <button
                    onClick={() => handleLike(dream.id)}
                    className="flex items-center gap-2 text-void/70 hover:text-void transition-colors"
                    data-testid={`like-btn-${dream.id}`}
                  >
                    <Heart className={`w-5 h-5 ${dream.liked_by_me ? 'fill-red-500 text-red-500' : ''}`} />
                    <span className="text-sm font-body">{dream.likes_count || 0}</span>
                  </button>
                  
                  <button
                    onClick={() => toggleComments(dream.id)}
                    className="flex items-center gap-2 text-void/70 hover:text-void transition-colors"
                    data-testid={`comments-btn-${dream.id}`}
                  >
                    <MessageCircle className={`w-5 h-5 ${showComments[dream.id] ? 'fill-void/20 text-void' : ''}`} />
                    <span className="text-sm font-body">{dream.comments_count || 0}</span>
                  </button>

                  <div className="flex items-center gap-2 text-void/40" data-testid={`views-${dream.id}`}>
                    <Eye className="w-5 h-5" />
                    <span className="text-sm font-body">{dream.views || 0}</span>
                  </div>
                </div>

                {/* Comments Section */}
                {showComments[dream.id] && (
                  <div className="mt-4 pt-4 border-t border-white/30">
                    {/* Add Comment */}
                    <div className="mb-4">
                      <Textarea
                        placeholder={t('feed.addComment')}
                        value={commentingOn === dream.id ? commentText : ""}
                        onChange={(e) => {
                          setCommentText(e.target.value);
                          setCommentingOn(dream.id);
                        }}
                        className="bg-white/50 border-transparent focus:border-ethereal focus:ring-2 focus:ring-ethereal/50 rounded-xl mb-2"
                      />
                      <Button
                        onClick={() => handleComment(dream.id)}
                        disabled={!commentText.trim()}
                        className="bg-void text-white hover:bg-void/90 rounded-full font-body text-sm"
                      >
                        {t('feed.postComment')}
                      </Button>
                    </div>

                    {/* Comments List */}
                    <div className="space-y-3">
                      {showComments[dream.id]?.map((comment) => (
                        <div key={comment.id} className="bg-white/30 rounded-2xl p-3">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-body font-medium text-void">{comment.user_name}</span>
                              <span className="text-xs text-void/50">{formatDate(comment.created_at)}</span>
                            </div>
                            {user?.is_admin && (
                              <button
                                onClick={() => handleDeleteComment(dream.id, comment.id)}
                                className="text-red-400 hover:text-red-600 transition-colors p-1"
                                title="Poista kommentti"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          <p className="text-sm text-void/80 font-body">{comment.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
